import React, { useEffect, useState } from 'react';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { ClaimsLogic } from '@/modules/idv';
import { useUserContext } from '@/modules/user/useUserContext';
import NotificationsInterstitial from './NotificationsInterstitial';
import PhoneLinkingInterstitial from './PhoneLinkingInterstitial';

export interface SharingInterstitials {
  /**
   * Share, unless the phone-linking interstitial is owed first, in which case
   * show it and do not share. Dismissing it does not share either: pasting a
   * one-time code straight into the SMS app would be jarring, so the sender
   * presses the share button again.
   */
  beforeSharing: (share: () => void) => void;
  /** Whether this share has already shown the phone-linking interstitial. */
  phoneLinkingShown: boolean;
  /** The interstitials themselves, for the screen to render. */
  interstitials: React.ReactNode;
}

/**
 * Which interstitials a share has shown: what `useSharingInterstitials` reads
 * and writes to show each one once per share.
 */
export interface InterstitialTracker {
  interstitialsShown: ReadonlySet<string>;
  recordInterstitialShown: (key: string) => void;
}

/**
 * `useSharingInterstitials` for the classic share wizard's steps, which keep
 * their record of what has been shown in the sharing session.
 */
export function useSharingInterstitialsWithWizardTracker(
  offerNotifications: boolean
): SharingInterstitials {
  const { interstitialsShown, recordInterstitialShown } =
    useShareWizardContext();
  return useSharingInterstitials(offerNotifications, {
    interstitialsShown,
    recordInterstitialShown,
  });
}

/**
 * The two interstitials a sender sees on the screen they start sharing from:
 * turning on notifications, shown on arrival, so a return nudge can bring them
 * back from the SMS app; and linking their phone, shown when they press the
 * button that starts sharing.
 *
 * Each shows once per share at most, as recorded in the caller's `tracker`
 * (the outbox keeps its own), and neither when suppressed. Phone linking
 * opens with "One more thing" when any other interstitial came first.
 * Notifications never shows on iOS: web push there needs a standalone
 * Home-Screen install, which is disabled (issue #475), so the permission
 * prompt cannot lead anywhere.
 *
 * `offerNotifications` - clients should pass false if the UI state isn't
 * appropriate for asking the user to be notified.
 */
export function useSharingInterstitials(
  offerNotifications: boolean,
  { interstitialsShown, recordInterstitialShown }: InterstitialTracker
): SharingInterstitials {
  const { isSuppressed } = useSuppressibleNotices();
  const { isIOS } = useDeviceInfo();
  const { claims } = useUserContext();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPhoneLinking, setShowPhoneLinking] = useState(false);
  const [showOneMoreThingMsg, setShowOneMoreThingMsg] = useState(false);
  const notificationsShown = interstitialsShown.has('notifications');
  const phoneLinkingShown = interstitialsShown.has('phoneLinking');

  useEffect(() => {
    if (notificationsShown || !offerNotifications || isIOS) return;
    const alreadyGranted =
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted';
    if (!isSuppressed('notifications') && !alreadyGranted) {
      setShowNotifications(true);
      recordInterstitialShown('notifications');
    }
  }, [
    notificationsShown,
    offerNotifications,
    isIOS,
    isSuppressed,
    recordInterstitialShown,
  ]);

  const beforeSharing = (share: () => void) => {
    if (
      !phoneLinkingShown &&
      !isSuppressed('phoneLinking') &&
      !ClaimsLogic.isPhoneVerified(claims)
    ) {
      // Counted before phone linking records itself, so it is any
      // interstitial that came earlier.
      setShowOneMoreThingMsg(interstitialsShown.size >= 1);
      setShowPhoneLinking(true);
      recordInterstitialShown('phoneLinking');
      return;
    }
    share();
  };

  const interstitials = (
    <>
      <NotificationsInterstitial
        isOpen={showNotifications}
        onDismiss={() => setShowNotifications(false)}
      />
      <PhoneLinkingInterstitial
        isOpen={showPhoneLinking}
        onDismiss={() => setShowPhoneLinking(false)}
        showOneMoreThingMsg={showOneMoreThingMsg}
      />
    </>
  );

  return { beforeSharing, phoneLinkingShown, interstitials };
}
